import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, type CalendarConnectionList, type CalendarConnectionView } from "@keydials/api-client";
import { HELP_ANCHORS } from "@keydials/ui";
import { api, getAccessToken } from "../auth/AuthContext";
import { SettingsCard } from "./SettingsCard";
import { CalendarChooser, calendarsLine } from "./CalendarChooser";
import { CalDavConnectForm } from "./CalDavConnectForm";
import { CalendarWriteControls } from "./CalendarWriteControls";
import { dialIdOf, lastPlace } from "../lib/lastPlace";

/** The same field look as the rest of Your settings. */
const field: React.CSSProperties = {
  font: "inherit",
  fontSize: 14,
  padding: "8px 11px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  minWidth: 0,
};

const PROVIDER_NAMES: Record<CalendarConnectionView["provider"], string> = {
  google: "Google Calendar",
  microsoft: "Outlook",
  ics: "iCal link",
  caldav: "CalDAV",
};

/**
 * Your connected calendars (KEY-736), in Your settings: the diaries a "My Seven Days" dial can show. A connection
 * belongs to the person, not the organisation, so it is made once here and offered on every dial they work on.
 *
 * Google and Outlook go through their own consent screens (a full-page round trip; the API sends the browser back
 * here with `?calendar=connected|failed`, then — KEY-769 — offers to put the new calendar on the dial they came from,
 * because connecting alone shows it on no dial). Apple/iCloud, Airbnb and Booking.com publish an iCal link, which is
 * pasted. The card says plainly what is read and for how long, because titles are a bigger ask than busy times.
 * Renders nothing when the feature is off.
 */
export function ConnectedCalendarsCard() {
  const [list, setList] = useState<CalendarConnectionList | null | undefined>(undefined);
  const [note, setNote] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  /** KEY-818: the account whose calendars are being chosen. */
  const [choosing, setChoosing] = useState<string | null>(null);
  const [icsUrl, setIcsUrl] = useState("");
  const [icsLabel, setIcsLabel] = useState("");
  const [adding, setAdding] = useState(false);
  /** KEY-769: the dial the person came from, when their just-connected calendar could be shown on it. */
  const [offer, setOffer] = useState<{ dialId: string; connectionId: string; back: string } | null>(null);
  const navigate = useNavigate();

  const refresh = async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      setList(await api.calendarConnections(token));
    } catch {
      setList(null);
    }
  };

  useEffect(() => {
    // Back from Google or Microsoft: say how it went once, then take the flag out of the address bar.
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("calendar");
    if (outcome) {
      setNote(
        outcome === "connected"
          ? "Calendar connected."
          : outcome === "writes-on"
            ? "Dials can now add appointments to that calendar."
            : outcome === "writes-refused"
              ? "The calendar is still connected, but adding appointments wasn't allowed. Try again and leave adding events ticked."
              : "That calendar couldn't be connected. Try again, or use its iCal link.",
      );
      params.delete("calendar");
      const query = params.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
      if (outcome === "connected") void offerDial();
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Connecting a calendar puts it on no dial by itself — that is a dial's own Settings → Calendars. If the person came
  // here from a dial that can show it, and their newest calendar isn't on it yet, offer that step here.
  const offerDial = async () => {
    const token = getAccessToken();
    const back = lastPlace();
    const dialId = dialIdOf(back);
    if (!token || !back || !dialId) return;
    try {
      const sources = await api.instanceCalendarSources(token, dialId);
      const newest = sources.myOtherConnections[sources.myOtherConnections.length - 1];
      if (sources.canAdd && newest) setOffer({ dialId, connectionId: newest.id, back });
    } catch {
      // Not a dial they can see any more: no offer, the calendar is still connected.
    }
  };

  const showOnDial = async () => {
    const token = getAccessToken();
    if (!token || !offer) return;
    setBusyId(offer.connectionId);
    try {
      await api.addInstanceCalendarSource(token, offer.dialId, offer.connectionId, false);
      navigate(offer.back, { replace: true });
    } catch (e) {
      setNote(e instanceof ApiError && e.message ? e.message : "That calendar couldn't be put on the dial. Try it from the dial's Settings → Calendars.");
      setOffer(null);
    } finally {
      setBusyId(null);
    }
  };

  if (!list || !list.enabled) return null;

  const connections = list.connections ?? [];
  const oauth = (list.providers ?? []).filter((p) => (p.provider === "google" || p.provider === "microsoft") && p.available);
  const caldavOffered = (list.providers ?? []).some((p) => p.provider === "caldav" && p.available);
  const icsOffered = (list.providers ?? []).some((p) => p.provider === "ics" && p.available);
  const days = list.windowDays ?? 8;
  const writeEnabled = list.writeEnabled === true;

  const connect = async (provider: "google" | "microsoft") => {
    const token = getAccessToken();
    if (!token) return;
    setNote(null);
    try {
      const { authorizationUrl } = await api.connectCalendar(token, provider);
      // KEY-768: replace, not assign — Your settings' place in the history is taken by the provider's sign-in, so the
      // browser's Back from the page we come back to doesn't land on it.
      window.location.replace(authorizationUrl);
    } catch (e) {
      setNote(e instanceof ApiError && e.message ? e.message : "That calendar can't be connected just now.");
    }
  };

  const addIcs = async () => {
    const token = getAccessToken();
    if (!token || !icsUrl.trim() || adding) return;
    setAdding(true);
    setNote(null);
    try {
      await api.addIcsCalendar(token, icsUrl.trim(), icsLabel.trim() || null);
      setIcsUrl("");
      setIcsLabel("");
      setNote("Calendar added.");
      await refresh();
    } catch (e) {
      setNote(e instanceof ApiError && e.message ? e.message : "That link couldn't be added.");
    } finally {
      setAdding(false);
    }
  };

  const sync = async (id: string) => {
    const token = getAccessToken();
    if (!token) return;
    setBusyId(id);
    try {
      await api.syncCalendar(token, id);
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string) => {
    const token = getAccessToken();
    if (!token) return;
    setBusyId(id);
    try {
      await api.disconnectCalendar(token, id);
      setConfirmRemove(null);
      await refresh();
    } finally {
      setBusyId(null);
    }
  };

  return (
    // `id` is the /settings#calendars target (the welcome page and Help both send people here); the anchor is
    // the Connect your calendar guide's.
    <div id="calendars" data-help-anchor={HELP_ANCHORS.connectedCalendars} style={{ scrollMarginTop: 72 }}>
    <SettingsCard
      title="Connected calendars"
      hint={`Diaries a "My Seven Days" dial can show. We read each event's title, time and place for the next ${days} days and keep them only for those days. ${writeEnabled ? "Nothing is written to a calendar unless you let dials add appointments to it." : "Nothing is ever written to your calendar."} Removing a calendar deletes everything read from it.`}
    >
      {connections.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {connections.map((c) => (
            <li key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <div style={{ fontSize: 13.5, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis" }}>{c.accountLabel}</div>
                <div style={{ fontSize: 12.5, color: c.lastSyncError ? "var(--danger, #b42318)" : "var(--text-muted)" }}>
                  {PROVIDER_NAMES[c.provider]}
                  {calendarsLine(c.calendarNames, c.canChooseCalendars) && ` · ${calendarsLine(c.calendarNames, c.canChooseCalendars)}`}
                  {" · "}
                  {c.lastSyncError ?? `${c.upcomingEventCount} coming up`}
                </div>
              </div>
              {c.canChooseCalendars && (
                <button
                  type="button"
                  className="kd-btn kd-btn-sm"
                  aria-expanded={choosing === c.id}
                  onClick={() => setChoosing(choosing === c.id ? null : c.id)}
                >
                  Calendars
                </button>
              )}
              <button type="button" className="kd-btn kd-btn-sm" disabled={busyId === c.id} onClick={() => void sync(c.id)}>
                {busyId === c.id ? "Working…" : "Sync now"}
              </button>
              {confirmRemove === c.id ? (
                <>
                  <button type="button" className="kd-btn kd-btn-sm" disabled={busyId === c.id} onClick={() => void remove(c.id)}>
                    Remove it
                  </button>
                  <button type="button" className="kd-btn kd-btn-sm" onClick={() => setConfirmRemove(null)}>Keep</button>
                </>
              ) : (
                <button type="button" className="kd-btn kd-btn-sm" disabled={busyId === c.id} onClick={() => setConfirmRemove(c.id)}>
                  Remove
                </button>
              )}
              {writeEnabled && c.supportsWrites && (
                <CalendarWriteControls
                  connection={c}
                  field={field}
                  onChanged={(message) => {
                    setNote(message);
                    void refresh();
                  }}
                />
              )}
              {choosing === c.id && (
                <CalendarChooser
                  connectionId={c.id}
                  onClose={() => setChoosing(null)}
                  onSaved={() => {
                    setChoosing(null);
                    setNote("Calendars saved. Your dials show them from now on.");
                    void refresh();
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {oauth.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {oauth.map((p) => (
            <button key={p.provider} type="button" className="kd-btn kd-btn-sm" onClick={() => void connect(p.provider as "google" | "microsoft")}>
              Connect {p.label}
            </button>
          ))}
        </div>
      )}

      {caldavOffered && (
        <CalDavConnectForm
          field={field}
          onAdded={(message) => {
            setNote(message);
            void refresh();
          }}
        />
      )}

      {icsOffered && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label htmlFor="ics-url" style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Or paste an iCal link (Airbnb, Booking.com, a published calendar…). It can only be read.
          </label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              id="ics-url"
              type="url"
              inputMode="url"
              placeholder="https://… or webcal://…"
              value={icsUrl}
              onChange={(e) => setIcsUrl(e.target.value)}
              style={{ ...field, flex: "2 1 240px" }}
            />
            <input
              aria-label="Name for this calendar"
              placeholder="Name (optional)"
              value={icsLabel}
              maxLength={200}
              onChange={(e) => setIcsLabel(e.target.value)}
              style={{ ...field, flex: "1 1 140px" }}
            />
            <button type="button" className="kd-btn kd-btn-sm" disabled={adding || !icsUrl.trim()} onClick={() => void addIcs()}>
              {adding ? "Reading…" : "Add"}
            </button>
          </div>
        </div>
      )}

      {note && <div role="status" style={{ fontSize: 13, color: "var(--text-muted)" }}>{note}</div>}
      {offer && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 13, color: "var(--text)" }}>
          <span>It isn't on the dial you came from yet. Others there will see your events as Busy.</span>
          <button type="button" className="kd-btn kd-btn-sm" disabled={busyId === offer.connectionId} onClick={() => void showOnDial()}>
            Show it on that dial
          </button>
          <button type="button" className="kd-btn kd-btn-sm" onClick={() => setOffer(null)}>Not now</button>
        </div>
      )}
    </SettingsCard>
    </div>
  );
}
